import * as Sentry from "@sentry/browser";
import {
  BillingAccount,
  BillingAPIError,
} from "@screencloud/studio-billing-sdk/client";
import {
  BillingV2NotConfiguredError,
  getBillingV2Client,
} from "src/domains/billing/billingClient";

/**
 * Which billing an org uses, read from billing v2 `me.get()`.
 *
 * @remarks
 * 200 means v2. 404 means the org is not on billing v2, so it is v1. Any other error fails open as v1, so existing
 * orgs keep today's behaviour when billing v2 has problems. `failedOpen` marks that last case: billing v2 did not
 * answer, so the org is only assumed to be v1.
 */
export type BillingVersionResult =
  | { version: "v2"; account: BillingAccount }
  | { version: "v1"; failedOpen?: true };

export const getBillingVersion = async (): Promise<BillingVersionResult> => {
  try {
    const account = await getBillingV2Client().me.get();
    return { version: "v2", account };
  } catch (error) {
    if (error instanceof BillingV2NotConfiguredError) {
      return { version: "v1" };
    }
    if (error instanceof BillingAPIError && error.status === 404) {
      return { version: "v1" };
    }
    // Fail open: treat as v1, but report it so a billing v2 outage is noticed.
    Sentry.captureException(error);
    return { version: "v1", failedOpen: true };
  }
};

export interface BillingVersionState {
  billingVersion: "v1" | "v2";
  billingV2Account: BillingAccount | null;
}

/** Maps a `getBillingVersion` result to the app context fields. v1 orgs get a `null` account. */
export const toBillingVersionState = (
  result: BillingVersionResult,
): BillingVersionState =>
  result.version === "v2"
    ? {
        billingVersion: "v2",
        billingV2Account: result.account,
      }
    : {
        billingVersion: "v1",
        billingV2Account: null,
      };

/**
 * Whether a reload should keep the billing state already in context.
 *
 * @remarks
 * Failing open as v1 is right at app load, but not for an org already known to be v2: one failed refetch would send
 * the user to the v1 billing page and drop v2 entitlement checks until a reload. Keep the v2 state instead.
 */
export const keepsCurrentBillingVersion = (
  result: BillingVersionResult,
  currentVersion: "v1" | "v2" | null,
): boolean =>
  result.version === "v1" &&
  result.failedOpen === true &&
  currentVersion === "v2";
